import "@/lib/extensions";
import $ from 'jquery';
import 'ajaxq';
import config from "@/services/config";
import user from "@/services/user";
import { flashError } from "@/services/helpers";

var addAuthProperties = async (t: ITrelloClient, cqsDto: any) => {    
    var cfg = await config(t).get();
    var hasToken = await user(t).getHasToken();
    
    if(cqsDto.IntegrationId == null) {
        cqsDto.IntegrationId = cfg.integrationId;
    }        
    if(cqsDto.PersonalAccessToken == null && hasToken) {
        var userData = await user(t).getUserData();
        cqsDto.PersonalAccessToken = userData.stobApiToken;
    }
    if(cqsDto.ExternalProjectId == null) {
        cqsDto.ExternalProjectId = t.getContext().board;
    }

    return cqsDto;
}

export enum RequestType {
    Async,
    Sync,
    Queued
}

export interface ICommandDispatcher {
    sendCommand(commandDto: any): Promise<any>;
    sendCommand(
        commandDto: any,
        options?: (opt: CommandDispatcherOptions) => void
    ): Promise<any>;
}

export interface ILocalCommandDispatcher extends ICommandDispatcher {
    hasHandlerFor(queryDto: any): boolean;
}

export abstract class CqsDto {
    public static CqsDtoType = "NotOverridden";

    constructor() {
        if(this.getCqsDtoType() == "NotOverridden") {
            throw Error("You mmust override CqsDtoType in cqs dtos");
        }
    }

    public getCqsDtoType = () => {
        return (<typeof CqsDto>this.constructor).CqsDtoType;
    }
}

export class CommandDispatcherOptions {
    public requestType: RequestType;
    public beforeSend: (xhr: any) => void;

    constructor() {
        this.requestType = RequestType.Queued;
        this.beforeSend = () => {};
    }
}

export interface IQueryExecutor {
    executeQuery(queryDto: any): Promise<any>;
    executeQuery(queryDto: any, requestType?: RequestType): Promise<any>;
    abortRunningQueries();
}

export interface ILocalQueryExecutor extends IQueryExecutor {
    hasHandlerFor(queryDto: any): boolean;
}

export interface IChannel {
    sendCommand(commandDto): Promise<any>;
    sendCommand(
        commandDto,
        options: (opt: CommandDispatcherOptions) => void
    ): Promise<any>;
    executeQuery<T>(queryDto: any): Promise<T>;
    executeQuery<T>(queryDto: any, requestType: RequestType): Promise<T>;
}

export class Channel implements IChannel {
    private command: ICommandDispatcher;
    private query: IQueryExecutor;
    private commandDefaultRequestType: RequestType;
    private queryDefaultRequestType: RequestType;

    constructor(command: ICommandDispatcher, query: IQueryExecutor) {
        this.command = command;
        this.query = query;
        this.commandDefaultRequestType = RequestType.Async;
    }

    public withDefaultRequestType(requestType: RequestType): Channel {
        this.commandDefaultRequestType = requestType;
        this.queryDefaultRequestType = requestType;
        return this;
    }

    public sendCommand(commandDto): Promise<any>;
    public sendCommand(
        commandDto: any,
        options?: (opt: CommandDispatcherOptions) => void
    ): Promise<any> {
        if (options) {
            return this.command.sendCommand(commandDto, options);
        } else {
            return this.command.sendCommand(commandDto, o => {
                o.requestType = this.commandDefaultRequestType;
            });
        }
    }

    public executeQuery(queryDto: any): Promise<any>;
    public executeQuery(
        queryDto: any,
        requestType?: RequestType
    ): Promise<any> {
        if (requestType) {
            return this.query.executeQuery(queryDto, requestType);
        } else {
            return this.query.executeQuery(
                queryDto,
                this.queryDefaultRequestType
            );
        }
    }

    public abortRunningQueries() {
        this.query.abortRunningQueries();
    }
}

export class CommandDispatcher implements ILocalCommandDispatcher {
    private t: ITrelloClient;

    hasHandlerFor(commandDto: CqsDto): boolean {
        var commandName = commandDto.getCqsDtoType().toLowerCase();
        var r = this.registry.firstOrDefault(x => x.cqsDtoName == commandName);
        if (r) {
            return true;
        }
        return false;
    }

    public abortRunningQueries() {}

    private registry: cqsHandlerRegisterer[] = [];

    constructor(t: ITrelloClient) {
        this.t = t;
    }

    register(commandType: string, commandHandler: Function): CommandDispatcher {
        var x = new cqsHandlerRegisterer(commandType, commandHandler);
        this.registry.push(x);
        return this;
    }

    sendCommand(commandDto: CqsDto): Promise<any> {
        var commandName = commandDto.getCqsDtoType().toLowerCase();
        var r = this.registry.firstOrDefault(x => x.cqsDtoName == commandName);
        if (r) {
            return r.cqsHandler(this.t, commandDto);
        }
    }
}

export class RemoteCommandDispatcher implements ICommandDispatcher {
    private commandUrl: string;
    private _throwsClientErrorOnServerError: boolean;
    private _addCommandNameToUrl: boolean;
    private t: ITrelloClient;

    constructor(
        t: ITrelloClient,
        commandUrl: string,
        throwsClientErrorOnServerError: boolean,
        addCommandNameToUrl: boolean
    ) {
        this.commandUrl = commandUrl;
        this._throwsClientErrorOnServerError = throwsClientErrorOnServerError;
        this._addCommandNameToUrl = addCommandNameToUrl;
        this.t = t;
    }

    public sendCommand(commandDto: CqsDto): Promise<any>;
    public async sendCommand(
        commandDto: CqsDto,
        options?: (opt: CommandDispatcherOptions) => void
    ): Promise<any> {
        var opts = new CommandDispatcherOptions();
        if (options) {
            options(opts);
        }

        var className = commandDto.getCqsDtoType();
        (<any>commandDto).CommandType = className; // CommandType property added dynamically
        commandDto = await addAuthProperties(this.t, commandDto);

        if (opts.requestType == RequestType.Sync) {
            return this.internalSendCommand(commandDto, false, opts);
        } else if (opts.requestType == RequestType.Async) {
            return this.internalSendCommand(commandDto, true, opts);
        } else if (opts.requestType == RequestType.Queued) {
            return this.internalSendQueuedCommand(commandDto, opts);
        }
        throw new Error("DynamicCommandDispatcher: Invalid request type");
    }

    private internalSendCommand(
        commandDto: CqsDto,
        async: boolean,
        opts: CommandDispatcherOptions
    ): Promise<any> {
        var jsonData = JSON.stringify(commandDto);
        var self = this;

        var url = this.commandUrl;
        if (this._addCommandNameToUrl) {
            var className = commandDto.getCqsDtoType().toLocaleLowerCase();
            url += "/" + className;
            if (commandDto.hasOwnProperty("id")) {
                if (commandDto["id"]) {
                    url += "/" + commandDto["id"];
                }
            }
        }

        return new Promise((resolve, reject) => {
            var jqPromise = $.ajax(url, {
                type: "POST",
                dataType: "json",
                contentType: "application/json",
                cache: false,
                async: async,
                beforeSend: opts.beforeSend,
                data: jsonData,
                success: result => {},
                error: (data, status, err) => {
                    if (self._throwsClientErrorOnServerError) {
                        reject(data.responseText);
                    }
                }
            });
            jqPromise.done(res => {
                resolve(res);
            });
        });
    }

    private internalSendQueuedCommand(
        commandDto: CqsDto,
        opts: CommandDispatcherOptions
    ): Promise<any> {
        var jsonData = JSON.stringify(commandDto);
        var self = this;

        var url = this.commandUrl;
        if (this._addCommandNameToUrl) {
            var className = commandDto.getCqsDtoType().toLocaleLowerCase();
            url += "/" + className;
            if (commandDto.hasOwnProperty("id")) {
                if (commandDto["id"]) {
                    url += "/" + commandDto["id"];
                }
            }
        }
        return new Promise((resolve, reject) => {
            var jqPromise = $.ajaxq("CQS_Queue", {
                url: url,
                type: "POST",
                dataType: "json",
                contentType: "application/json",
                cache: false,
                beforeSend: opts.beforeSend,
                data: jsonData,
                success: result => {},
                error: (data, status, err) => {
                    if ($.ajaxq) {
                        (<any>$.ajaxq).abort("CQS_Queue");
                    }
                    if (self._throwsClientErrorOnServerError) {
                        //reject();
                        
                        // Ez itt elrontja a? a jquery ajax-ot, utána nem fog működni, illetve nem fut le a megfelelő handler ami kiírja a usernek h. internal server error volt.
                        //throw new Error("Server error: " + data.responseText);
                    }
                }
            });
            jqPromise.done(res => {
                resolve(res);
            });
        });
    }
}

export class CompositeCommandDispatcher implements ICommandDispatcher {
    private localDispatcher: CommandDispatcher;
    private remoteDispatcher: ICommandDispatcher;

    constructor(
        t: ITrelloClient,
        commandUrl: string,
        throwsClientErrorOnServerError: boolean,
        addCommandNameToUrl: boolean
    ) {
        this.localDispatcher = new CommandDispatcher(t);
        this.remoteDispatcher = new RemoteCommandDispatcher(
            t,
            commandUrl,
            throwsClientErrorOnServerError,
            addCommandNameToUrl
        );
    }

    sendCommand(commandDto: CqsDto): Promise<any>;
    sendCommand(
        commandDto: CqsDto,
        options: (opt: CommandDispatcherOptions) => void
    ): Promise<any>;
    sendCommand(
        commandDto: CqsDto,
        options?: (opt: CommandDispatcherOptions) => void
    ): Promise<any> {
        if (this.localDispatcher.hasHandlerFor(commandDto)) {
            return this.localDispatcher.sendCommand(commandDto);
        }
        return this.remoteDispatcher.sendCommand(commandDto, options);
    }

    register(
        commandType: string,
        commandHandler: Function
    ): CompositeCommandDispatcher {
        this.localDispatcher.register(commandType, commandHandler);
        return this;
    }
}

export class QueryExecutor implements ILocalQueryExecutor {
    hasHandlerFor(queryDto: CqsDto): boolean {
        var queryName = queryDto.getCqsDtoType().toLowerCase();
        var r = this.registry.firstOrDefault(x => x.cqsDtoName == queryName);
        if (r) {
            return true;
        }
        return false;
    }

    private t: ITrelloClient;

    public abortRunningQueries() {}

    private registry: cqsHandlerRegisterer[] = [];

    constructor(t: ITrelloClient) {
        this.t = t;
    }

    register(queryName: string, queryHandler: Function): QueryExecutor {
        var x = new cqsHandlerRegisterer(queryName, queryHandler);
        this.registry.push(x);
        return this;
    }

    executeQuery(queryDto: CqsDto): Promise<any> {
        var queryName = queryDto.getCqsDtoType().toLowerCase();
        var r = this.registry.firstOrDefault(x => x.cqsDtoName == queryName);
        if (r) {
            var handlerResult = r.cqsHandler(this.t, queryDto);
            return handlerResult;
        }
    }
}

export class RemoteQueryExecutor implements IQueryExecutor {
    private queryUrl: string;
    private _throwsClientErrorOnServerError: boolean;
    private runningQueries: JQueryXHR[] = [];
    private _addQueryNameToUrl: boolean;
    private t: ITrelloClient;

    constructor(
        t: ITrelloClient,
        queryUrl: string,
        throwsClientErrorOnServerError: boolean,
        addQueryNameToUrl: boolean
    ) {
        this.queryUrl = queryUrl;
        this._throwsClientErrorOnServerError = throwsClientErrorOnServerError;
        this._addQueryNameToUrl = addQueryNameToUrl;
        this.t = t;
    }

    executeQuery(queryDto: CqsDto): Promise<any>;
    async executeQuery(queryDto?: CqsDto, requestType?: RequestType): Promise<any> {
        var className = queryDto.getCqsDtoType();
        (<any>queryDto).QueryType = className; // QueryType property added dynamically
        queryDto = await addAuthProperties(this.t, queryDto);

        if (requestType == null || requestType == undefined) {
            return this.internalExecuteQuery(queryDto, true);
        } else if (requestType == RequestType.Sync) {
            return this.internalExecuteQuery(queryDto, false);
        } else if (requestType == RequestType.Async) {
            return this.internalExecuteQuery(queryDto, true);
        } else if (requestType == RequestType.Queued) {
            return this.internalExecuteQueuedQuery(queryDto);
        }
        throw new Error("DynamicQueryExecutor: Invalid request type");
    }

    abortRunningQueries() {
        (<any>$.ajaxq).abort("CQS_Queue");
        this.runningQueries.forEachItem(item => item.abort());
        this.runningQueries = [];
    }

    private async internalExecuteQuery(queryDto: CqsDto, async: boolean): Promise<any> {
        var jsonData = JSON.stringify(queryDto);
        var self = this;

        var url = this.queryUrl;
        if (this._addQueryNameToUrl) {
            var className = queryDto.getCqsDtoType().toLocaleLowerCase();
            url += "/" + className;
            if (queryDto.hasOwnProperty("id")) {
                if (queryDto["id"]) {
                    url += "/" + queryDto["id"];
                }
            }
        }
        return new Promise((resolve, reject) => {
            var xhr = $.ajax(url, {
                type: "POST",
                method: "POST",
                dataType: "json",
                contentType: "application/json",
                cache: false,
                async: async,
                data: jsonData,
                success: result => {},
                error: (data, status, err) => {
                    if (self._throwsClientErrorOnServerError) {
                        reject(data.responseText);
                    }
                }
            });
            self.storeRunningQuery(xhr);
            xhr.done(res => {
                resolve(res);
            });
        });
    }

    private internalExecuteQueuedQuery: (queryDto: CqsDto) => Promise<any> = async (queryDto: any) => {
        var jsonData = JSON.stringify(queryDto);
        var self = this;

        var url = this.queryUrl;
        if (this._addQueryNameToUrl) {
            var className = queryDto.getCqsDtoType().toLocaleLowerCase();
            url += "/" + className;
            if (queryDto.hasOwnProperty("id")) {
                if (queryDto["id"]) {
                    url += "/" + queryDto["id"];
                }
            }
        }
        return new Promise((resolve, reject) => {
            var jqPromise = $.ajaxq("CQS_Queue", {
                url: url,
                method: "POST",
                type: "POST",
                dataType: "json",
                contentType: "application/json",
                cache: false,
                data: jsonData,
                success: result => {},
                error: (data, status, err) => {
                    if (status != "abort") {
                        if (self._throwsClientErrorOnServerError) {
                            reject(data.responseText);
                        }
                    }
                }
            });
            jqPromise.done(res => {
                resolve(res);
            });
        });
    }

    private storeRunningQuery(runningQuery: JQueryXHR) {
        var self = this;
        runningQuery.always(() => {
            self.runningQueries = self.runningQueries.filter(
                item => item != runningQuery
            );
        });

        self.runningQueries.push(runningQuery);
    }
}

export class CompositeQueryExecutor implements IQueryExecutor {
    private localExecutor: QueryExecutor;
    private realExecutor: IQueryExecutor;

    constructor(
        t: ITrelloClient,
        queryUrl: string,
        throwsClientErrorOnServerError: boolean,
        addQueryNameToUrl: boolean
    ) {
        this.localExecutor = new QueryExecutor(t);
        this.realExecutor = new RemoteQueryExecutor(
            t,
            queryUrl,
            throwsClientErrorOnServerError,
            addQueryNameToUrl
        );
    }

    executeQuery(queryDto: CqsDto): Promise<any>;
    executeQuery(queryDto?: CqsDto, requestType?: RequestType): Promise<any> {
        if (this.localExecutor.hasHandlerFor(queryDto)) {
            var res = this.localExecutor.executeQuery(queryDto);
            return res;
        }
        return this.realExecutor.executeQuery(queryDto, requestType);
    }

    abortRunningQueries() {
        this.realExecutor.abortRunningQueries();
    }

    register(queryType: string, queryHandler: Function): CompositeQueryExecutor {
        this.localExecutor.register(queryType, queryHandler);
        return this;
    }
}

class cqsHandlerRegisterer {
    cqsDtoName: string;
    cqsHandler: Function;

    constructor(cqsDtoType, handler) {
        this.cqsDtoName = cqsDtoType.toLowerCase();
        this.cqsHandler = handler;
    }
}
